import { useCallback, useEffect, useRef, useState } from 'react'
import { toast } from 'sonner'
import { getFriendlyError } from '@/utils/getFriendlyError'
import {
  createBloco,
  createBlocoKanbanColumn,
  createBlocoLabel,
  deleteBloco,
  deleteBlocoKanbanColumn,
  deleteBlocoLabel,
  fetchBlocoDetail,
  fetchBlocoKanbanColumns,
  fetchBlocoLabels,
  fetchBlocos,
  fetchBlocosBadgeCount,
  moveBlocoKanban,
  postBlocoMessage,
  reorderBlocoKanbanColumns,
  updateBloco,
  updateBlocoKanbanColumn,
  updateBlocoMembers,
  uploadBlocoAttachment,
} from '@/services/blocosApi'
import type {
  Bloco,
  BlocoDetail,
  BlocoKanbanColumn,
  BlocoLabel,
  BlocoType,
  CreateBlocoPayload,
  PostBlocoMessagePayload,
  UpdateBlocoMembersPayload,
} from '@/types/bloco'
import { useApiSessionReady } from '@/hooks/useApiSessionReady'

const POLL_MS = 60_000

function mergeBlocoInList(items: Bloco[], updated: Bloco): Bloco[] {
  return items.map((item) => (item.id === updated.id ? { ...item, ...updated } : item))
}

export function useBlocos() {
  const sessionReady = useApiSessionReady()
  const [items, setItems] = useState<Bloco[]>([])
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [badgeCount, setBadgeCount] = useState(0)
  const [threadDetail, setThreadDetail] = useState<BlocoDetail | null>(null)
  const [threadLoading, setThreadLoading] = useState(false)
  const [threadSaving, setThreadSaving] = useState(false)
  const [labels, setLabels] = useState<BlocoLabel[]>([])
  const [labelsLoading, setLabelsLoading] = useState(false)
  const [kanbanColumns, setKanbanColumns] = useState<BlocoKanbanColumn[]>([])
  const [columnsLoading, setColumnsLoading] = useState(false)
  const pageLoadRef = useRef<Promise<void> | null>(null)

  const refreshBadgeCount = useCallback(async () => {
    try {
      const count = await fetchBlocosBadgeCount()
      setBadgeCount(count)
    } catch {
      /* badge opcional */
    }
  }, [])

  const loadLabels = useCallback(async () => {
    setLabelsLoading(true)
    try {
      const data = await fetchBlocoLabels()
      setLabels(data)
    } catch {
      setLabels([])
    } finally {
      setLabelsLoading(false)
    }
  }, [])

  const loadKanbanColumns = useCallback(async () => {
    setColumnsLoading(true)
    try {
      const data = await fetchBlocoKanbanColumns()
      setKanbanColumns(data)
    } catch {
      setKanbanColumns([])
    } finally {
      setColumnsLoading(false)
    }
  }, [])

  const loadBlocos = useCallback(async () => {
    setLoading(true)
    try {
      const data = await fetchBlocos()
      setItems(data)
      await refreshBadgeCount()
    } catch (err) {
      setItems([])
      toast.error(getFriendlyError(err, 'Não foi possível carregar seus blocos'))
    } finally {
      setLoading(false)
    }
  }, [refreshBadgeCount])

  const loadBlocosPageData = useCallback(async () => {
    if (pageLoadRef.current) return pageLoadRef.current

    pageLoadRef.current = (async () => {
      setLoading(true)
      setLabelsLoading(true)
      setColumnsLoading(true)
      try {
        const [blocosData, labelsData, columnsData] = await Promise.all([
          fetchBlocos(),
          fetchBlocoLabels(),
          fetchBlocoKanbanColumns(),
        ])
        setItems(blocosData)
        setLabels(labelsData)
        setKanbanColumns(columnsData)
        await refreshBadgeCount()
      } catch (err) {
        setItems([])
        setLabels([])
        setKanbanColumns([])
        toast.error(getFriendlyError(err, 'Não foi possível carregar seus blocos'))
      } finally {
        setLoading(false)
        setLabelsLoading(false)
        setColumnsLoading(false)
      }
    })().finally(() => {
      pageLoadRef.current = null
    })

    return pageLoadRef.current
  }, [refreshBadgeCount])

  const loadBlocoThread = useCallback(async (id: string) => {
    setThreadLoading(true)
    try {
      const detail = await fetchBlocoDetail(id)
      setThreadDetail(detail)
      setItems((prev) => mergeBlocoInList(prev, detail))
      return detail
    } catch {
      toast.error('Não foi possível abrir a conversa do bloco')
      setThreadDetail(null)
      throw new Error('thread load failed')
    } finally {
      setThreadLoading(false)
    }
  }, [])

  const addBloco = useCallback(
    async (payload: CreateBlocoPayload, attachmentFiles: File[] = []) => {
      setSaving(true)
      try {
        const created = await createBloco(payload)
        if (attachmentFiles.length > 0) {
          await Promise.all(attachmentFiles.map((file) => uploadBlocoAttachment(created.id, file)))
          const refreshed = await fetchBlocoDetail(created.id)
          setItems((prev) => [refreshed, ...prev.filter((b) => b.id !== created.id)])
        } else {
          setItems((prev) => [created, ...prev])
        }
        await refreshBadgeCount()
        toast.success('Bloco salvo')
        return created
      } catch (err) {
        toast.error(getFriendlyError(err, 'Não foi possível salvar o bloco'))
        throw new Error('create failed')
      } finally {
        setSaving(false)
      }
    },
    [refreshBadgeCount],
  )

  const moveBlocoToColumn = useCallback(
    async (id: string, kanbanColumnId: string) => {
      try {
        const updated = await moveBlocoKanban(id, kanbanColumnId)
        setItems((prev) => mergeBlocoInList(prev, updated))
        setThreadDetail((prev) => (prev?.id === id ? { ...prev, ...updated } : prev))
        await refreshBadgeCount()
      } catch {
        toast.error('Não foi possível mover a tarefa')
      }
    },
    [refreshBadgeCount],
  )

  const addKanbanColumn = useCallback(async (name: string, accent: string) => {
    try {
      const created = await createBlocoKanbanColumn({ name, accent, kind: 'open' })
      setKanbanColumns((prev) => [...prev, created].sort((a, b) => a.sortOrder - b.sortOrder))
      toast.success('Coluna criada')
      return created
    } catch (err) {
      toast.error(getFriendlyError(err, 'Não foi possível criar a coluna'))
      throw new Error('column create failed')
    }
  }, [])

  const renameKanbanColumn = useCallback(async (columnId: string, name: string) => {
    try {
      const updated = await updateBlocoKanbanColumn(columnId, { name })
      setKanbanColumns((prev) => prev.map((c) => (c.id === columnId ? updated : c)))
      toast.success('Coluna atualizada')
    } catch (err) {
      toast.error(getFriendlyError(err, 'Não foi possível renomear a coluna'))
    }
  }, [])

  const removeKanbanColumn = useCallback(async (columnId: string) => {
    try {
      await deleteBlocoKanbanColumn(columnId)
      setKanbanColumns((prev) => prev.filter((c) => c.id !== columnId))
      await loadBlocos()
      toast.success('Coluna removida')
    } catch (err) {
      toast.error(getFriendlyError(err, 'Não foi possível remover a coluna'))
    }
  }, [loadBlocos])

  const reorderKanbanColumns = useCallback(async (columnIds: string[]) => {
    try {
      const data = await reorderBlocoKanbanColumns({ columnIds })
      setKanbanColumns(data)
    } catch {
      toast.error('Não foi possível reordenar as colunas')
    }
  }, [])

  const addLabel = useCallback(async (name: string, color: string) => {
    try {
      const created = await createBlocoLabel({ name, color })
      setLabels((prev) => [...prev, created].sort((a, b) => a.name.localeCompare(b.name)))
      toast.success('Etiqueta criada')
      return created
    } catch (err) {
      toast.error(getFriendlyError(err, 'Não foi possível criar a etiqueta'))
      throw new Error('label create failed')
    }
  }, [])

  const removeLabel = useCallback(async (labelId: string) => {
    try {
      await deleteBlocoLabel(labelId)
      setLabels((prev) => prev.filter((l) => l.id !== labelId))
      setItems((prev) =>
        prev.map((item) => ({
          ...item,
          labels: item.labels?.filter((l) => l.id !== labelId),
        })),
      )
      toast.success('Etiqueta removida')
    } catch {
      toast.error('Não foi possível remover a etiqueta')
    }
  }, [])

  const assignBlocoLabels = useCallback(async (id: string, labelIds: string[]) => {
    try {
      const updated = await updateBloco(id, { labelIds })
      setItems((prev) => mergeBlocoInList(prev, updated))
      setThreadDetail((prev) => (prev?.id === id ? { ...prev, ...updated } : prev))
    } catch {
      toast.error('Não foi possível atualizar as etiquetas')
    }
  }, [])

  const completeBloco = useCallback(
    async (id: string) => {
      try {
        const updated = await updateBloco(id, { status: 'done' })
        setItems((prev) => mergeBlocoInList(prev, updated))
        setThreadDetail((prev) => (prev?.id === id ? { ...prev, ...updated } : prev))
        await refreshBadgeCount()
        toast.success('Bloco encerrado — movido para Histórico')
      } catch {
        toast.error('Não foi possível concluir o bloco')
      }
    },
    [refreshBadgeCount],
  )

  const removeBloco = useCallback(
    async (id: string) => {
      try {
        await deleteBloco(id)
        setItems((prev) => prev.filter((b) => b.id !== id))
        setThreadDetail((prev) => (prev?.id === id ? null : prev))
        await refreshBadgeCount()
        toast.success('Bloco removido')
      } catch {
        toast.error('Não foi possível remover o bloco')
      }
    },
    [refreshBadgeCount],
  )

  const sendThreadMessage = useCallback(
    async (id: string, payload: PostBlocoMessagePayload) => {
      setThreadSaving(true)
      try {
        const message = await postBlocoMessage(id, payload)
        const detail = await fetchBlocoDetail(id)
        setThreadDetail(detail)
        setItems((prev) => mergeBlocoInList(prev, detail))
        await refreshBadgeCount()
        return message
      } catch {
        toast.error('Não foi possível enviar a mensagem')
        throw new Error('message failed')
      } finally {
        setThreadSaving(false)
      }
    },
    [refreshBadgeCount],
  )

  const saveThreadMembers = useCallback(async (id: string, payload: UpdateBlocoMembersPayload) => {
    setThreadSaving(true)
    try {
      const updated = await updateBlocoMembers(id, payload)
      setItems((prev) => mergeBlocoInList(prev, updated))
      setThreadDetail((prev) => (prev?.id === id ? { ...prev, ...updated } : prev))
      toast.success('Colegas atualizados')
      return updated
    } catch {
      toast.error('Não foi possível atualizar os colegas')
      throw new Error('members failed')
    } finally {
      setThreadSaving(false)
    }
  }, [])

  const uploadThreadAttachment = useCallback(async (blocoId: string, file: File) => {
    setThreadSaving(true)
    try {
      await uploadBlocoAttachment(blocoId, file)
      const detail = await fetchBlocoDetail(blocoId)
      setThreadDetail(detail)
      setItems((prev) => mergeBlocoInList(prev, detail))
      toast.success('Anexo enviado')
    } catch (err) {
      toast.error(getFriendlyError(err, 'Não foi possível enviar o anexo'))
      throw new Error('attachment failed')
    } finally {
      setThreadSaving(false)
    }
  }, [])

  useEffect(() => {
    if (!sessionReady) return
    refreshBadgeCount()
    const timer = window.setInterval(refreshBadgeCount, POLL_MS)
    return () => window.clearInterval(timer)
  }, [refreshBadgeCount, sessionReady])

  return {
    items,
    loading,
    saving,
    badgeCount,
    labels,
    labelsLoading,
    kanbanColumns,
    columnsLoading,
    threadDetail,
    threadLoading,
    threadSaving,
    loadBlocos,
    loadLabels,
    loadKanbanColumns,
    loadBlocosPageData,
    loadBlocoThread,
    addBloco,
    moveBlocoToColumn,
    addKanbanColumn,
    renameKanbanColumn,
    removeKanbanColumn,
    reorderKanbanColumns,
    addLabel,
    removeLabel,
    assignBlocoLabels,
    completeBloco,
    removeBloco,
    sendThreadMessage,
    saveThreadMembers,
    uploadThreadAttachment,
    clearThread: () => setThreadDetail(null),
    refreshBadgeCount,
  }
}

export type { BlocoType }
